import { useEffect, useState, useCallback } from 'react';
import { useMedia } from 'react-use';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import app from '@frontastic/catwalk/src/js/app/app';
import { useGeoIp } from './useGeoIp';
import { useNoticesContext } from '../provider/notices/NoticesContext';
import TagManager from '../classes/TagManager';
import { throwFetchErrors } from '../utils/throwFetchErrors';
import { parseProductsForNosto } from '../utils/parseProductsForNosto';
import { sdk } from '../../../sdk';

const promoInitialState = {
  loadingAdd: false,
  loadingRemove: false,
  type: null,
  message: null,
  id: null,
};

export const useCart = () => {
  const isMobile = useMedia('(max-width: 680px)', false);
  const [loading, setLoading] = useState(false);
  const [promoState, setPromoState] = useState(promoInitialState);
  const [showCart, setShowCart] = useState(false);
  const [showCartConfirmation, setShowCartConfirmation] = useState(false);
  const [cartData, setCartData] = useState({});
  const { getCountryToUpdate } = useGeoIp();
  const { setNotices } = useNoticesContext();

  // We need to set both initial cart and available shipping methods
  // so the whole logic of the app is not broken
  const [initialCart, initialShippingMethods, context] = useSelector(
    (state) => [state.cart.cart, state.cart?.availableShippingMethods || [], state.app.context],
    shallowEqual,
  );

  const dispatch = useDispatch();

  const giftCardMethodId = context?.project?.data?.virtualShippingMethodId;
  const collectionMethodId = context?.project?.data?.collectionShippingMethodId;

  const defaultShippingMethod =
    initialShippingMethods?.data?.find(
      (m) => m.projectSpecificData.isDefault,
    ) ||
    initialShippingMethods?.data?.find(
      (m) =>
        // Don't actually do any check here but just use the first one.
        // The old code defined these variables before using them, so they were
        // still undefined when used here.
        /*![collectionMethodId, giftCardMethodId].includes(m?.shippingMethodId)*/ true,
    );

  const setShippingMethod = useCallback(
    async ({ cart, availableShippingMethods = [] }) => {
      const isGiftCart = cart?.lineItems?.some((item) => item.type === 'gift-card');

      const shippingMethods =
        !!initialShippingMethods?.data && !!initialShippingMethods?.data?.length
          ? initialShippingMethods?.data
          : availableShippingMethods;

      const method = isGiftCart
        ? availableShippingMethods?.find((m) => m.shippingMethodId === giftCardMethodId)
        : shippingMethods?.find((m) => ![collectionMethodId, giftCardMethodId].includes(m?.shippingMethodId));

      if ((!cart?.shippingMethod && !!shippingMethods.length) || isGiftCart) {
        try {
          await updateCartAddress({
            shippingMethodName: method?.shippingMethodId,
          });
        } catch (error) {
          setNotices({
            type: 'error',
            code: 'error',
            message: error?.message,
          });
        }
      }
    },
    [initialShippingMethods],
  );

  const resetPromoState = () => setPromoState(promoInitialState);

  const closePromoWithTimeout = () => {
    setTimeout(() => {
      setPromoState((prevState) => ({
        ...prevState,
        message: null,
      }));
    }, 5000);
  };

  const addToCart = async (
    variant,
    count,
    option = null,
    callback = null,
    shouldShow = true,
    errorCallback = null,
    doNotUpdateData = false,
  ) => {
    try {
      const data = await sdk.callAction({
        actionName: 'cart/addToCart',
        payload: {
          variant: {
            sku: variant.sku,
            count: count ?? 1,
          },
          storeKey: option?.channel?.id,
        },
      });

      if (!data) {
        setNotices({
          type: 'error',
          code: 'noData',
          message: 'No data received',
        });
      }

      if (data.isError) {
        throw new Error(data.error.message);
      }

      const wrappedCart = {
        cart: data.data,
        addedItems: [],
        availableShippingMethods: data.data.availableShippingMethods,
      };

      dispatch({
        type: 'CartApi.Cart.add.success',
        data: wrappedCart,
      })
      !doNotUpdateData && setCartData(wrappedCart);
      await setShippingMethod(wrappedCart);
      callback && callback();
      if (isMobile) {
        return setShowCartConfirmation(shouldShow);
      }
      setShowCart(shouldShow);

      return wrappedCart;
    } catch (error) {
      setNotices({
        type: 'error',
        code: error.code,
        message: error.message,
      });
      errorCallback && errorCallback();
    }

    return null;
  };

  const removeFromCart = (lineItem, item, callback = null, alert = true) => {
    return app.getApi().request(
      'POST',
      'Frontastic.CartApi.Cart.removeLineItem',
      { ownErrorHandler: true },
      lineItem,
      async (data) => {
        if (!data) {
          setNotices({
            type: 'error',
            code: 'noData',
            message: 'No data received',
          });
        }
        setCartData(data);
        callback && callback();
        if (!data.cart.lineItems.length) {
          for await (let item of data.cart.discountCodes) {
            await removeDiscountFromCart(item.discountId);
          }
        }
        await setShippingMethod(data);
        if (alert) {
          setNotices({
            type: 'success',
            code: 'cart.message.remove',
            message: 'Product has been removed from cart',
          });
        }
        if (item) {
          TagManager.removeFromCart(item, data.cart);
        }
      },
      (error) => {
        setNotices({
          type: 'error',
          code: error.code,
          message: error.message,
        });
      },
    );
  };

  const updateCartItem = (update, callback = null) => {
    return app.getApi().request(
      'POST',
      'Frontastic.CartApi.Cart.updateLineItem',
      { ownErrorHandler: true },
      update,
      async (data) => {
        if (!data) {
          setNotices({
            type: 'error',
            code: 'noData',
            message: 'No data received',
          });
        }
        await setShippingMethod(data);
        setCartData(data);
        callback && callback();
      },
      (error) => {
        setNotices({
          type: 'error',
          code: error.code,
          message: error.message,
        });
      },
    );
  };

  const updateCartAddress = async (cartInformation, errorCallback) => {
    return await app.getApi().request(
      'POST',
      'Frontastic.CartApi.Cart.update',
      { ownErrorHandler: true },
      cartInformation,
      (data) => {
        setCartData(data);
      },
      (error) => {
        setNotices({
          type: 'error',
          code: error.code,
          message: error.message,
        });
        errorCallback && errorCallback(error);
        throwFetchErrors();
      },
    );
  };

  const addCustomLineItemToCart = (props) => {
    const { productId = '', value = '', senderName = '', receiverName = '', email = '', message = '' } = props;

    setLoading(true);

    return app.getApi().request(
      'POST',
      'Apg.GiftCard.Post.VoucherLineItem',
      { ownErrorHandler: true },
      {
        'product-id': productId,
        value,
        'sender-name': senderName,
        'receiver-name': receiverName,
        email: email,
        message: message,
      },
      async (data) => {
        setCartData(data);
        setLoading(false);

        // this is to recalculate shipping methods
        await updateCartAddress({
          shippingMethodName: context?.project?.data?.virtualShippingMethodId,
        });
        if (isMobile) {
          return setShowCartConfirmation(true);
        }
        setShowCart(true);
      },
      (error) => {
        setNotices({
          type: 'error',
          message: error?.message,
        });
        setLoading(false);
        return error;
      },
    );
  };

  const removeCustomLineItemToCart = ({ itemId }) => {
    setLoading(true);

    return app.getApi().request(
      'POST',
      'Apg.GiftCard.Post.Remove.VoucherLineItem',
      { ownErrorHandler: true },
      {
        'item-id': itemId,
      },
      async (data) => {
        if (!data) return;
        setCartData(data);
        if (!data.cart?.lineItems?.length || !data.cart?.lineItems?.some((item) => item.type === 'gift-card')) {
          await updateCartAddress({
            shippingMethodName: defaultShippingMethod?.shippingMethodId,
          });
        }
        setLoading(false);
      },
      (error) => {
        setLoading(false);
        setNotices({
          type: 'error',
          code: error.code,
          message: error.message,
        });
        return error;
      },
    );
  };

  const addDiscountToCart = (promoCode, errorCallback) => {
    if (!promoCode) {
      setPromoState((prevState) => ({
        ...prevState,
        message: 'Enter valid promo code.',
        type: 'error',
      }));
      return;
    }

    setPromoState((prevState) => ({
      ...prevState,
      loadingAdd: true,
      message: null,
    }));

    return app.getApi().request(
      'POST',
      'Frontastic.CartApi.Cart.redeemDiscount',
      { ownErrorHandler: true, code: promoCode },
      null,
      async (data) => {
        if (data.cart.discountCodes.some(({ code }) => code === promoCode)) {
          const { cart } = data;
          const { projectSpecificData, discountCodes } = cart || {};
          const { discountCodesOriginal, discounts } = projectSpecificData || {};

          const appliedDiscount = data.cart.discountCodes.find(({ code }) => code === promoCode);
          const originalDiscount = discountCodesOriginal?.find(({ id }) => id === appliedDiscount.discountId);

          const discount =
            originalDiscount &&
            discounts?.filter((d) => d.type === 'cart-discount' && d.id === originalDiscount.originalId);

          const discountCode = originalDiscount && discountCodes.filter((x) => x.discountId === originalDiscount.id);

          const notAppliedDiscount = discount.find(
            (d) => (d.amount === 0 && d.target !== 'shipping') || (d.target === 'shipping' && !d?.isValidShipping),
          );

          const notMatchCartDiscount = discountCode.find((x) => x.state === 'DoesNotMatchCart');

          if (notAppliedDiscount || notMatchCartDiscount) {
            setPromoState((prevState) => ({
              ...prevState,
              type: 'error',
              loadingAdd: false,
              message: notMatchCartDiscount
                ? `Sorry, this promo can't be applied. Try again or contact us.`
                : `'${promoCode}' promo code not applied`,
            }));

            const promoOriginalId = notMatchCartDiscount
              ? { id: notMatchCartDiscount.discountId }
              : discountCodesOriginal?.find(({ originalId }) => originalId === notAppliedDiscount.id);

            return await removeDiscountFromCart(promoOriginalId.id, false);
          }

          await setShippingMethod(data);

          setCartData(data);

          setPromoState((prevState) => ({
            ...prevState,
            type: 'success',
            loadingAdd: false,
            message: `Promo code (${promoCode}) applied successfully.`,
          }));

          TagManager.promoCodeAttempt({
            promoCodeResult: 'Success',
            promoCode: promoCode,
            cartItem: data.cart.lineItems,
            amount: discount?.[0]?.amount || 0,
          });
          closePromoWithTimeout();
        }
      },
      (error) => {
        setPromoState((prevState) => ({
          ...prevState,
          type: 'error',
          loadingAdd: false,
          message:
            error.code === 'commercetools.DiscountCodeNonApplicable'
              ? `′${promoCode}′ is not a valid code`
              : error.message,
        }));
        TagManager.promoCodeAttempt({
          promoCode: promoCode,
          promoCodeResult: 'Failed',
        });
        errorCallback &&
          errorCallback({
            type: 'error',
            message: error.message,
          });
        closePromoWithTimeout();
        return error;
      },
    );
  };

  const removeDiscountFromCart = async (discountId, showStatusMessage = true) => {
    if (!discountId) {
      return;
    }
    if (showStatusMessage) {
      setPromoState((prevState) => ({
        ...prevState,
        message: null,
        loadingRemove: true,
        type: 'remove',
        id: discountId,
      }));
    }
    return app.getApi().request(
      'POST',
      'Frontastic.CartApi.Cart.removeDiscount',
      { ownErrorHandler: true },
      { discountId: discountId },
      async (data) => {
        await setShippingMethod(data);
        await setCartData(data);
        showStatusMessage &&
          setPromoState((prevState) => ({
            ...prevState,
            type: 'success',
            loadingRemove: false,
            message: 'Promo code has been successfully removed.',
            id: null,
          }));

        closePromoWithTimeout();
        return data;
      },
      (error) => {
        setNotices({
          type: 'error',
          code: error.code,
          message: error.message,
        });
        setPromoState((prevState) => ({
          ...prevState,
          type: 'error',
          loadingRemove: false,
          message: error.message,
          id: null,
        }));
        return error;
      },
    );
  };

  const checkout = (cartInformation) => {
    const store = app.getStore();
    store.dispatch({
      type: 'CartApi.Cart.loading',
    });

    store.dispatch({
      type: 'UI.setCheckoutPanelLoading',
      data: true,
    });

    return app
      .getApi()
      .request(
        'POST',
        'Frontastic.CartApi.Cart.checkout',
        { ownErrorHandler: true },
        cartInformation,
        (data) => {
          setCartData(data);
          store.dispatch({
            type: 'CartApi.Cart.checkout.success',
            data: data,
          });
        },
        (error) => {
          store.dispatch({
            type: 'CartApi.Cart.checkout.error',
            error: error,
          });
        },
      )
      .then(
        (data) => {
          app.getRouter().push('Frontastic.Frontend.Master.Checkout.finished', {
            order: data.order.orderId,
            token: (data.order && data.order.projectSpecificData && data.order.projectSpecificData.viewToken) || null,
          });
          setCartData(data);
          return data;
        },
        (error) => {
          setNotices({
            type: 'error',
            code: error.code,
            message: error.message,
          });
          return error;
        },
      )
      .finally(() => {
        store.dispatch({
          type: 'UI.setCheckoutPanelLoading',
          data: false,
        });
      });
  };

  const getCart = () => {
    return app.getApi().request('GET', 'Frontastic.CartApi.Cart.get', { ownErrorHandler: true }, null, (data) => {
      setCartData(data);
      return data;
    });
  };

  const parseCardDataForNosto = useCallback((data) => {
    const { cart } = data || {};
    if (cart && cart.lineItems?.length > 0) {
      const prods = cart.lineItems;
      return parseProductsForNosto(prods, cart?.currency);
    }
  }, []);

  useEffect(() => {
    if (initialCart?.isComplete()) {
      const { shippingAddress } = initialCart.data || {};
      if (!shippingAddress?.country) {
        const countryToUpdate = getCountryToUpdate();
        updateCartAddress({
          billing:
            context?.session?.account?.addresses
              ?.find(address => address.isDefaultBillingAddress) ?? {
              country: countryToUpdate,
            },
          shipping:
            context?.session?.account?.addresses
              ?.find(address => address.isDefaultShippingAddress) ?? {
              country: countryToUpdate,
            },
        });
      } else {
        setCartData({
          cart: initialCart.data,
          availableShippingMethods: initialShippingMethods.data,
        });
      }
    }
  }, [initialCart]);

  return {
    loading,
    setLoading,
    cartData,
    showCart,
    promoState,
    setCartData,
    setShowCart,
    setPromoState,
    showCartConfirmation,
    setShowCartConfirmation,
    addCustomLineItemToCart,
    removeCustomLineItemToCart,
    addToCart,
    removeFromCart,
    updateCartItem,
    updateCartAddress,
    addDiscountToCart,
    removeDiscountFromCart,
    resetPromoState,
    getCart,
    checkout,
    parseCardDataForNosto,
    setShippingMethod,
  };
};
